import { Chip, CircularProgress, Grid, MenuItem, Select, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Step from "@mui/material/Step";
import StepContent from "@mui/material/StepContent";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { updateSubscription } from "../../../services/subscriptions.service";
import { useToast } from "../../../ui-components/ToastProvider";
import { parseDate, parseDateHifen } from "../../../utils/parseDate";

function getSubscriptionPriceDinamic(
  spaceType,
  dayCount = 1,
  extraChairs = 0,
  extraTables = 0,
  extraBike = false,
  extraTrailer = false,
  eventData
) {
  const d = Math.max(1, parseInt(dayCount, 10) || 1);
  const qChairs = Math.max(0, Number(extraChairs) || 0);
  const qTables = Math.max(0, Number(extraTables) || 0);

  const pricing = eventData?.[spaceType] || {};

  const sumForDays = (p1, p2) => {
    const one = Number(p1) || 0;
    const two = Number(p2) || 0;
    if (d === 1) return one;
    if (d === 2) return two > 0 ? two : one * 2;
    const baseTwoDays = two > 0 ? two : one * 2;
    return baseTwoDays + (d - 2) * one;
  };

  const base = sumForDays(
    pricing.subscription_price_1,
    pricing.subscription_price_2
  );
  const chair =
    qChairs * sumForDays(pricing.chair_price_1, pricing.chair_price_2);
  const table =
    qTables * sumForDays(pricing.table_price_1, pricing.table_price_2);
  const bike = extraBike ? (Number(pricing.bike) || 0) : 0;
  const trailer = extraTrailer ? (Number(pricing.trailer) || 0) : 0;

  return Math.round((base + chair + table + bike + trailer) * 100) / 100;
}

function parseCurrency(x) {
  if (typeof x !== "string") {
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
  }
  const cleaned = x
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

const PriceTable = ({ eventData }) => {
  let data = eventData;
  try {
    if (typeof data === "string") data = JSON.parse(data);
  } catch (e) {}

  const tendas1 = data?.["autoral"] || {
    subscription_price_1: 0,
    subscription_price_2: 0,
    table_price_1: 0,
    table_price_2: 0,
    chair_price_1: 0,
    chair_price_2: 0,
    percentage: 0,
  };

  const tendas2 = data?.["alimentacao"] || {
    subscription_price_1: 0,
    subscription_price_2: 0,
    table_price_1: 0,
    table_price_2: 0,
    chair_price_1: 0,
    chair_price_2: 0,
    percentage: 0,
    bike: 0,
    trailer: 0,
  };

  const formatBRL = (value) => {
    const num = Number(value);
    if (!isFinite(num)) return "—";
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  const renderSection = (title, pricing, hideOneDay = false) => (
    <Grid item xs={12} md={12}>
      <Paper
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          height: "100%",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#24324D", mb: 1 }}
        >
          {title}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Grid container spacing={2}>
            {!hideOneDay && (
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Chip label="1 dia" size="small" />
                    <Typography sx={{ fontWeight: 700 }}>
                      {formatBRL(pricing.subscription_price_1)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gap: 1 }}>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography sx={{ color: "#475569" }}>Mesa</Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {formatBRL(pricing.table_price_1)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography sx={{ color: "#475569" }}>Cadeira</Typography>
                      <Typography sx={{ fontWeight: 600 }}>
                        {formatBRL(pricing.chair_price_1)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={hideOneDay ? 12 : 6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Chip label="2 dias" size="small" />
                  <Typography sx={{ fontWeight: 700 }}>
                    {formatBRL(pricing.subscription_price_2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "grid", gap: 1 }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ color: "#475569" }}>Mesa</Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatBRL(pricing.table_price_2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography sx={{ color: "#475569" }}>Cadeira</Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatBRL(pricing.chair_price_2)}
                    </Typography>
                  </Box>
                  {hideOneDay && (
                    <>
                      <Box
                        sx={{ display: "flex", justifyContent: "space-between" }}
                      >
                        <Typography sx={{ color: "#475569" }}>Bike</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {formatBRL(pricing.bike)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", justifyContent: "space-between" }}
                      >
                        <Typography sx={{ color: "#475569" }}>Trailer</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {formatBRL(pricing.trailer)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between", px: 1 }}>
            <Typography sx={{ color: "#475569" }}>% Vendas</Typography>
            <Typography sx={{ fontWeight: 600 }}>
              {pricing.percentage}%
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );

  return (
    <div className="flex flex-col gap-3">
      <Grid container spacing={2}>
        {renderSection("Espaço Exclusivo (2mx2m) MARCAS AUTORAIS", tendas1)}
        {renderSection(
          "Espaço Exclusivo (2mx2m) PRAÇA DE ALIMENTAÇÃO",
          tendas2,
          true
        )}
      </Grid>
    </div>
  );
};

export default function UpdateSubscriptionForm({
  selectedSub,
  handleClose,
  events,
}) {
  const { showToast } = useToast();
  const [activeStep, setActiveStep] = React.useState(0);
  const [formData, setFormData] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const chosenevent =
    events.find(
      (el) =>
        el.starts_at === selectedSub.start_date ||
        el.ends_at === selectedSub.end_date
    ) || events[0];

  React.useEffect(() => {
    setFormData((state) => ({
      ...state,
      user_id: selectedSub.user_id,
      event_id: selectedSub.event_id,
      start_date: selectedSub.start_date,
      end_date: selectedSub.end_date,
      extra_table: selectedSub.extra_table || false,
      extra_chair: selectedSub.extra_chair || false,
      extra_bike: selectedSub.extra_bike || false,
      extra_trailer: selectedSub.extra_trailer || false,
      space_type: selectedSub.space_type || "autoral",
      table_quantity: selectedSub.table_quantity || 0,
      chair_quantity: selectedSub.chair_quantity || 0,
      notes: selectedSub.notes || "",
      items: selectedSub.items || "",
    }));
  }, [selectedSub]);

  const steps = [
    {
      label: "Informaçoes sobre a pré-inscrição e tabela de preços",
      description: (
        <>
          <PriceTable eventData={chosenevent?.event_data} />
        </>
      ),
    },
    {
      label: "Selecionar dia/dias",
      description: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <TextField
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
            id="start_date"
            name="start_date"
            label="Data de inicio da participacao"
            type={"date"}
            onChange={(e) => {
              const newStart = e.target.value;
              setFormData((state) => {
                const newState = {
                  ...state,
                  start_date: newStart,
                };
                if (state.end_date && newStart > state.end_date) {
                  newState.end_date = newStart;
                }
                return newState;
              });
            }}
            value={
              formData.start_date || parseDateHifen(selectedSub.start_date)
            }
            autoComplete="start_date"
            variant="outlined"
            fullWidth
            disabled={formData.space_type === "alimentacao"}
            helperText={
              formData.space_type === "alimentacao"
                ? "Praça de Alimentação exige participação nos 2 dias do evento."
                : undefined
            }
            InputProps={{
              inputProps: {
                min: parseDateHifen(chosenevent.starts_at),
                max: parseDateHifen(chosenevent.ends_at),
              },
            }}
            required
          />
          <TextField
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
            id="end_date"
            name="end_date"
            label="Data de Fim da participacao"
            type={"date"}
            value={formData.end_date || parseDateHifen(selectedSub.end_date)}
            onChange={(e) => {
              setFormData((state) => ({ ...state, end_date: e.target.value }));
            }}
            autoComplete="start_date"
            variant="outlined"
            fullWidth
            disabled={formData.space_type === "alimentacao"}
            InputProps={{
              inputProps: {
                min:
                  formData.start_date || parseDateHifen(chosenevent.starts_at),
                max: parseDateHifen(chosenevent.ends_at),
              },
            }}
            required
          />
        </div>
      ),
    },
    {
      label: "Escolher tipo de espaço",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="space_type"
            id="space_type"
            name="space_type"
            defaultValue={formData.space_type || "autoral"}
            value={formData.space_type || "autoral"}
            onChange={(e) => {
              const val = e.target.value;
              setFormData((state) => {
                const newState = {
                  ...state,
                  space_type: val,
                };
                if (val === "alimentacao") {
                  newState.start_date = parseDateHifen(chosenevent.starts_at);
                  newState.end_date = parseDateHifen(chosenevent.ends_at);
                }
                return newState;
              });
            }}
            fullWidth
          >
            <MenuItem key={1} value={"autoral"}>
              Espaço Exclusivo (2mx2m) MARCAS AUTORAIS
            </MenuItem>
            <MenuItem key={2} value={"alimentacao"}>
              Espaço Exclusivo (2mx2m) PRAÇA DE ALIMENTAÇÃO
            </MenuItem>
          </Select>
        </>
      ),
    },
    {
      label: "Deseja cadeira extra?",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="chair_quantity"
            id="chair_quantity"
            name="chair_quantity"
            value={formData.chair_quantity || 0}
            onChange={(e) => {
              const qty = Number(e.target.value);
              setFormData((state) => ({
                ...state,
                extra_chair: qty > 0,
                chair_quantity: qty,
              }));
            }}
            fullWidth
          >
            <MenuItem value={0}>Não desejo cadeira extra</MenuItem>
            <MenuItem value={1}>Desejo uma cadeira</MenuItem>
            <MenuItem value={2}>Desejo duas cadeiras</MenuItem>
          </Select>
        </>
      ),
    },
    {
      label: "Deseja mesa?",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="table_quantity"
            id="table_quantity"
            name="table_quantity"
            value={formData.table_quantity || 0}
            onChange={(e) => {
              const qty = Number(e.target.value);
              setFormData((state) => ({
                ...state,
                extra_table: qty > 0,
                table_quantity: qty,
              }));
            }}
            fullWidth
          >
            <MenuItem value={0}>Não desejo mesa extra</MenuItem>
            <MenuItem value={1}>Desejo uma mesa</MenuItem>
            <MenuItem value={2}>Desejo duas mesas</MenuItem>
          </Select>
        </>
      ),
    },
    {
      label: "Deseja Bike?",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="extra_bike"
            id="extra_bike"
            name="extra_bike"
            value={formData.extra_bike || 0}
            onChange={(e) => {
              const val = Number(e.target.value);
              setFormData((state) => ({
                ...state,
                extra_bike: val,
              }));
            }}
            fullWidth
          >
            <MenuItem value={0}>Não desejo bike</MenuItem>
            <MenuItem value={1}>Desejo bike</MenuItem>
          </Select>
        </>
      ),
    },
    {
      label: "Deseja Trailer?",
      description: (
        <>
          <Select
            variant="outlined"
            sx={{
              my: 2,
              borderRadius: 2,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&:hover fieldset": { borderColor: "#cbd5e1" },
            }}
            required
            labelId="extra_trailer"
            id="extra_trailer"
            name="extra_trailer"
            value={formData.extra_trailer || 0}
            onChange={(e) => {
              const val = Number(e.target.value);
              setFormData((state) => ({
                ...state,
                extra_trailer: val,
              }));
            }}
            fullWidth
          >
            <MenuItem value={0}>Não desejo trailer</MenuItem>
            <MenuItem value={1}>Desejo trailer</MenuItem>
          </Select>
        </>
      ),
    },
    {
      label:
        "Descreva TODOS os itens que você pretende levar para vender na Praceria",
      description: (
        <>
          <TextField
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
            id="items"
            name="items"
            label="Itens"
            placeholder="Liste um a um os itens que você produz e pretende levar na Praceria."
            onChange={(e) => {
              setFormData((state) => ({ ...state, items: e.target.value }));
            }}
            autoComplete="items"
            variant="outlined"
            defaultValue={formData.items}
            value={formData.items}
            rows={4}
            multiline
            fullWidth
            required
          />
        </>
      ),
    },
    {
      label: "Alguma observação?",
      description: (
        <>
          <TextField
            sx={{
              my: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#cbd5e1",
              },
            }}
            id="notes"
            name="notes"
            label="Notas"
            onChange={(e) => {
              setFormData((state) => ({ ...state, notes: e.target.value }));
            }}
            autoComplete="notes"
            variant="outlined"
            defaultValue={formData.notes}
            value={formData.notes}
            rows={4}
            multiline
            fullWidth
            required
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    if (formData.start_date && formData.end_date) {
      const toNum = parseCurrency;
      let parsed = {};
      try {
        parsed =
          typeof chosenevent.event_data === "string"
            ? JSON.parse(chosenevent.event_data || "{}")
            : chosenevent.event_data || {};
      } catch {
        parsed = {};
      }

      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const days = Math.max(
        1,
        Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      );
      const chairsQty = formData.extra_chair
        ? toNum(formData.chair_quantity || 0)
        : 0;
      const tablesQty = formData.extra_table
        ? toNum(formData.table_quantity || 0)
        : 0;

      const amount = getSubscriptionPriceDinamic(
        formData.space_type || "1_tendas",
        days,
        chairsQty,
        tablesQty,
        formData.extra_bike,
        formData.extra_trailer,
        parsed
      );
      setFormData((state) => ({ ...state, amount: amount }));
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    await updateSubscription(formData, selectedSub.id);
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              {step.description}
              <Box sx={{ mb: 2 }}>
                <div>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{
                      mt: 1,
                      mr: 1,
                      backgroundColor: "#24324D",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: "#1a2538" },
                    }}
                  >
                    {index === steps.length - 1 ? "Finalizar" : "Continuar"}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1, color: "#24324D" }}
                  >
                    Voltar
                  </Button>
                </div>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
      {activeStep === steps.length && (
        <Paper square elevation={0} sx={{ p: 3 }}>
          <Typography my={2}>
            Confirme suas escolhas para editar sua pré-inscrição:
          </Typography>
          <Grid>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Data de início da sua participação:{" "}
              </span>
              {parseDate(formData.start_date)}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Data de fim da sua participação:{" "}
              </span>
              {parseDate(formData.end_date)}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Tipo de espaço:{" "}
              </span>
              {formData.space_type === "autoral"
                ? "Espaço Exclusivo (2mx2m) MARCAS AUTORAIS"
                : formData.space_type === "alimentacao"
                ? "Espaço Exclusivo (2mx2m) PRAÇA DE ALIMENTAÇÃO"
                : formData.space_type}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>Mesa: </span>
              {formData.extra_table ? "Sim" : "Não"}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Quantas mesas:{" "}
              </span>
              {formData.table_quantity}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Cadeira Extra:{" "}
              </span>
              {formData.extra_chair ? "Sim" : "Não"}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Quantas cadeiras:{" "}
              </span>
              {formData.chair_quantity}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Bike:{" "}
              </span>
              {formData.extra_bike ? "Sim" : "Não"}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Trailer:{" "}
              </span>
              {formData.extra_trailer ? "Sim" : "Não"}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>Itens: </span>
              {formData.items}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>
                Observação:{" "}
              </span>
              {formData.notes}
            </Typography>
            <Typography>
              <span style={{ color: "#24324D", fontWeight: 600 }}>Valor: </span>{" "}
              R$ {formData.amount},00
            </Typography>
            <Typography></Typography>
          </Grid>

          <Grid flex>
            <Button
              variant="outlined"
              disabled={isSubmitting}
              onClick={() => {
                handleClose();
              }}
              sx={{
                mt: 1,
                mr: 1,
                color: "#24324D",
                borderColor: "#24324D",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { borderColor: "#1a2538", color: "#1a2538" },
              }}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              onClick={async () => {
                if (isSubmitting) return;
                setIsSubmitting(true);
                try {
                  await handleSubmit();
                  showToast("Pré-inscrição alterada com sucesso!", {
                    severity: "success",
                  });
                  handleClose();
                  window.location.reload();
                } catch (err) {
                  showToast(
                    err?.message ||
                      "Falha ao alterar a pré-inscrição. Tente novamente.",
                    { severity: "error" },
                  );
                } finally {
                  setIsSubmitting(false);
                }
              }}
              sx={{
                mt: 1,
                mr: 1,
                backgroundColor: "#24324D",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#1a2538" },
              }}
            >
              {isSubmitting ? "Enviando..." : "Alterar Pré-inscrição"}
            </Button>
          </Grid>
        </Paper>
      )}
    </Box>
  );
}
